import { useCustom, useTranslate } from '@refinedev/core';
import { Alert, Button, Space, Table, Tag, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { PATHWAY_OUTCOME_FIELDS, type PathwayOutcomeRow } from './model';

/**
 * E34 · PathwayOutcomes — "Practice — Pathways — Outcomes" (cc-homework, chapter "Practice — Homework board
 * (portal, E34)", screen e34e0007-0001-4000-8000-000000000014): per pathway, how many enrolled, running,
 * completed, withdrew; the completion rate and the median days — the practice's own evidence, and what THIS
 * WAY UP reports to clinicians. Reached from the library's Outcomes.
 *
 * Behavior lines implemented here: format a copy is listed right under its original, indented, "copy of …"
 * — the two funnels side by side; the therapist chose who got which (no random assignment, Q-e34-19 A),
 * i18n pathways.outcomes.copyOf; i18n pathways.outcomes.*. A retired template keeps its outcomes, marked.
 */
export const PathwayOutcomesPage = () => {
  const t = useTranslate();
  const { data, isFetching, isError } = useCustom<PathwayOutcomeRow[]>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: { operation: 'pathwayOutcomes', fields: PATHWAY_OUTCOME_FIELDS },
  });
  const rows = data?.data ?? [];
  const n = (v: number | null | undefined, id: string, suffix = '') => (
    <span data-testid={id}>{v === null || v === undefined ? '—' : `${v}${suffix}`}</span>
  );

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }} data-testid="pathway-outcomes">
      <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
        <div>
          <Typography.Title level={3} style={{ marginBottom: 0 }}>
            {t('pathways.outcomes.title')}
          </Typography.Title>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {t('pathways.outcomes.intro')}
          </Typography.Paragraph>
        </div>
        <Link to="/pathways">
          <Button data-testid="pathway-outcomes-back">{t('pathways.outcomes.back')}</Button>
        </Link>
      </Space>
      {isError ? <Alert type="error" showIcon message={t('pathways.outcomes.loadFailed')} /> : null}
      <Table<PathwayOutcomeRow>
        rowKey="pathwayId"
        loading={isFetching}
        dataSource={rows}
        pagination={false}
        locale={{ emptyText: t('pathways.outcomes.empty') }}
        onRow={(r) => ({ 'data-testid': `pathway-outcome-${r.pathwayId}`, 'data-depth': r.depth } as never)}
        columns={[
          {
            title: t('pathways.outcomes.column.name'),
            render: (_, r) => (
              <Space direction="vertical" size={0} style={{ paddingInlineStart: r.depth ? 24 : 0 }}>
                <Typography.Text strong={!r.depth}>
                  {r.depth ? '↳ ' : ''}
                  {r.pathwayName}
                  {r.retired ? (
                    <Tag style={{ marginInlineStart: 8 }}>{t('pathways.library.retired')}</Tag>
                  ) : null}
                </Typography.Text>
                {r.copiedFrom ? (
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: 12 }}
                    data-testid={`pathway-outcome-copy-${r.pathwayId}`}
                  >
                    {t('pathways.outcomes.copyOf', { name: r.copiedFromName ?? r.copiedFrom })}
                  </Typography.Text>
                ) : null}
              </Space>
            ),
          },
          { title: t('pathways.outcomes.column.enrolled'), width: 100, render: (_, r) => n(r.enrolledCount, `pathway-outcome-enrolled-${r.pathwayId}`) },
          { title: t('pathways.outcomes.column.running'), width: 100, render: (_, r) => n(r.runningCount, `pathway-outcome-running-${r.pathwayId}`) },
          { title: t('pathways.outcomes.column.completed'), width: 110, render: (_, r) => n(r.completedCount, `pathway-outcome-completed-${r.pathwayId}`) },
          { title: t('pathways.outcomes.column.withdrawn'), width: 110, render: (_, r) => n(r.withdrawnCount, `pathway-outcome-withdrawn-${r.pathwayId}`) },
          { title: t('pathways.outcomes.column.rate'), width: 120, render: (_, r) => n(r.completionRate, `pathway-outcome-rate-${r.pathwayId}`, '%') },
          { title: t('pathways.outcomes.column.median'), width: 150, render: (_, r) => n(r.medianDaysToComplete, `pathway-outcome-median-${r.pathwayId}`) },
        ]}
      />
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {t('pathways.outcomes.legend')}
      </Typography.Text>
    </Space>
  );
};
